import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Users, Clock, MapPin, ChevronRight, Check, X, Search, Filter, User, Briefcase, AlertCircle } from 'lucide-react';
import { useApp } from '../contexts/AppContext';

const SchedulerScreen = () => {
  const { 
    events, 
    contractors,
    activeForm, 
    importedForms,
    getAvailableContractors, 
    getEventAssignments,
    assignContractor,
    unassignContractor
  } = useApp();

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('Crew');
  const [filterAvailable, setFilterAvailable] = useState(true);

  // Clear search when switching events to avoid confusing matches
  useEffect(() => {
    setSearchTerm('');
  }, [selectedEvent]);

  const positions = [
    'Producer', 'Director', 'Camera', 'Utility', 'A2', 'EIC', 'Engineer', 'Tape', 'Graphics', 'Replay', 'Audio', 'Stage Manager', 'Runner', 'PA', 'Other'
  ];

  const activeFormData = importedForms.find(f => f.id === activeForm);

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
  };

  const handleAssignContractor = (contractorId) => {
    if (!selectedEvent) return;
    assignContractor(selectedEvent.id, contractorId, selectedPosition);
  };

  const handleUnassignContractor = (contractorId) => {
    if (!selectedEvent) return;
    unassignContractor(selectedEvent.id, contractorId);
  };

  const getFilteredContractors = () => {
    if (!selectedEvent) return [];
    
    const available = getAvailableContractors(selectedEvent.id);
    const assigned = getEventAssignments(selectedEvent.id);
    
    let filtered = filterAvailable 
      ? available.filter(c => !assigned.find(a => a.contractor?.id === c.id))
      : contractors;

    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  if (!activeForm || !activeFormData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center glass rounded-lg p-8 border border-ops-border">
          <AlertCircle className="w-10 h-10 text-ops-accent-primary mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-ops-text-primary mb-1">
            No form selected
          </h3>
          <p className="text-ops-text-secondary text-sm">
            Import a response form and select it on the Import tab to begin scheduling.
          </p>
        </div>
      </div>
    );
  }

  const formEvents = events.filter(e => e.formId === activeForm);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
      {/* Events List */}
      <div className="lg:col-span-1 space-y-4">
        <div className="glass rounded-lg border border-ops-border p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-semibold text-ops-text-primary flex items-center gap-2">
                <Calendar className="w-5 h-5 text-ops-accent-primary" />
                Event List
              </h2>
              <p className="text-xs text-ops-text-tertiary">
                Select an event to assign crew
              </p>
            </div>
            <span className="text-xs text-ops-text-tertiary">
              {formEvents.length} events
            </span>
          </div>
          <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1 custom-scrollbar">
            {formEvents.map(event => (
              <button
                key={event.id}
                onClick={() => handleSelectEvent(event)}
                className={`
                  w-full text-left rounded-md px-3 py-2 border text-sm flex items-center justify-between
                  transition-all
                  ${selectedEvent?.id === event.id
                    ? 'border-ops-accent-primary bg-ops-bg-elevated text-ops-text-primary'
                    : 'border-ops-border bg-ops-bg-card text-ops-text-secondary hover:border-ops-accent-primary/70 hover:bg-ops-bg-elevated/60'
                  }
                `}
              >
                <div>
                  <div className="font-medium">{event.name}</div>
                  <div className="flex items-center space-x-2 text-xs text-ops-text-tertiary mt-0.5">
                    <Clock className="w-3 h-3" />
                    <span>{event.date}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-ops-text-tertiary" />
              </button>
            ))}

            {formEvents.length === 0 && (
              <div className="text-xs text-ops-text-tertiary py-6 text-center">
                No events detected in this form.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assignment Panel */}
      <div className="lg:col-span-2 space-y-4">
        <div className="glass rounded-lg border border-ops-border p-4">
          {selectedEvent ? (
            <>
              {/* Event Header */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-ops-accent-primary" />
                    <h2 className="text-lg font-semibold text-ops-text-primary">
                      {selectedEvent.name}
                    </h2>
                  </div>
                  <div className="flex items-center space-x-3 mt-1 text-xs text-ops-text-tertiary">
                    <div className="flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>{selectedEvent.date}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <MapPin className="w-3 h-3" />
                      <span>Event {formEvents.findIndex(e => e.id === selectedEvent.id) + 1} of {formEvents.length}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center space-x-2 text-xs">
                    <span className="text-ops-text-tertiary">Position:</span>
                    <select
                      value={selectedPosition}
                      onChange={(e) => setSelectedPosition(e.target.value)}
                      className="bg-ops-bg-card border border-ops-border text-ops-text-secondary rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ops-accent-primary"
                    >
                      {positions.map(pos => (
                        <option key={pos} value={pos}>{pos}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={() => setFilterAvailable(prev => !prev)}
                    className={`
                      inline-flex items-center px-2.5 py-1.5 rounded-md border text-xs font-medium transition-colors
                      ${filterAvailable
                        ? 'bg-ops-accent-primary/10 border-ops-accent-primary text-ops-accent-primary'
                        : 'bg-ops-bg-card border-ops-border text-ops-text-tertiary hover:border-ops-accent-primary/70 hover:text-ops-text-primary'
                      }
                    `}
                  >
                    <Filter className="w-3 h-3 mr-1" />
                    {filterAvailable ? 'Available Only' : 'All Contractors'}
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {/* Contractor Search / List */}
                <div className="space-y-3">
                  <div className="flex items-center mb-1">
                    <h3 className="text-sm font-semibold text-ops-text-primary flex items-center gap-2">
                      <Users className="w-4 h-4 text-ops-accent-primary" />
                      Contractor Pool
                    </h3>
                    <span className="ml-auto text-xs text-ops-text-tertiary">
                      {getFilteredContractors().length} shown
                    </span>
                  </div>
                  <div className="relative mb-2">
                    <Search className="w-4 h-4 text-ops-text-tertiary absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search available contractors by name or email..."
                      className="pl-9 pr-3 py-2 w-full rounded-md bg-ops-bg-card border border-ops-border text-ops-text-primary placeholder:text-ops-text-tertiary focus:outline-none focus:ring-1 focus:ring-ops-accent-primary focus:border-ops-accent-primary text-sm"
                    />
                  </div>
                  <div className="border border-ops-border rounded-md bg-ops-bg-card max-h-[360px] overflow-y-auto custom-scrollbar">
                    {getFilteredContractors().map(contractor => (
                      <button
                        key={contractor.id}
                        onClick={() => handleAssignContractor(contractor.id)}
                        className="w-full px-3 py-2 text-left border-b border-ops-border/50 last:border-b-0 hover:bg-ops-bg-elevated/60 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-ops-text-primary flex items-center gap-2">
                              <User className="w-3.5 h-3.5 text-ops-accent-primary" />
                              {contractor.name}
                            </div>
                            <div className="flex items-center text-xs text-ops-text-tertiary mt-0.5">
                              <span className="truncate max-w-[180px]">{contractor.email}</span>
                              {contractor.primaryRole && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-ops-bg-elevated border border-ops-border text-[10px] text-ops-text-tertiary ml-2">
                                  <Briefcase className="w-3 h-3 mr-1" />
                                  {contractor.primaryRole}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-ops-bg-elevated border border-ops-border text-[10px] text-ops-text-tertiary mb-1">
                              <Users className="w-3 h-3 mr-1" />
                              {selectedPosition}
                            </span>
                            <span className="inline-flex items-center text-[10px] text-ops-accent-primary">
                              <Check className="w-3 h-3 mr-1" />
                              Assign
                            </span>
                          </div>
                        </div>
                      </button>
                    ))}

                    {getFilteredContractors().length === 0 && (
                      <div className="px-3 py-8 text-center text-xs text-ops-text-tertiary">
                        No contractors match this view. Try adjusting your filters or search.
                      </div>
                    )}
                  </div>
                </div>

                {/* Assigned Contractors */}
                <div className="space-y-3">
                  <div className="flex items-center mb-1">
                    <h3 className="text-sm font-semibold text-ops-text-primary flex items-center gap-2">
                      <Users className="w-4 h-4 text-ops-accent-primary" />
                      Assigned Crew
                    </h3>
                    <span className="ml-auto text-xs text-ops-text-tertiary">
                      {getEventAssignments(selectedEvent.id).length} assigned
                    </span>
                  </div>
                  <div className="border border-ops-border rounded-md bg-ops-bg-card max-h-[360px] overflow-y-auto custom-scrollbar">
                    {getEventAssignments(selectedEvent.id).map((assignment, index) => (
                      <div
                        key={`${assignment.contractorId}-${index}`}
                        className="px-3 py-2 border-b border-ops-border/50 last:border-b-0 flex items-center justify-between hover:bg-ops-bg-elevated/60 transition-colors"
                      >
                        <div>
                          <div className="text-sm font-medium text-ops-text-primary flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-ops-accent-primary" />
                            {assignment.contractor?.name || 'Unknown'}
                          </div>
                          <div className="flex items-center text-xs text-ops-text-tertiary mt-0.5">
                            <span className="truncate max-w-[180px]">
                              {assignment.contractor?.email || 'No email'}
                            </span>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-ops-bg-elevated border border-ops-border text-[10px] text-ops-text-tertiary ml-2">
                              <Briefcase className="w-3 h-3 mr-1" />
                              {assignment.position}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleUnassignContractor(assignment.contractorId)}
                          className="inline-flex items-center px-2 py-1 rounded-md border border-ops-border text-[11px] text-ops-text-tertiary hover:bg-ops-bg-elevated hover:border-ops-accent-danger hover:text-ops-accent-danger transition-colors"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Remove
                        </button>
                      </div>
                    ))}

                    {getEventAssignments(selectedEvent.id).length === 0 && (
                      <div className="px-3 py-8 text-center text-xs text-ops-text-tertiary">
                        No crew assigned yet. Pick contractors from the left panel to build your team.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-80">
              <div className="text-center">
                <Calendar className="w-16 h-16 text-ops-text-tertiary mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-ops-text-primary mb-2">Select an Event</h3>
                <p className="text-ops-text-secondary">Choose an event from the left panel to begin assigning crew</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchedulerScreen;
